import { createSelector } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import type { RootState } from '@/store';

import { LOCK_IT_IN_CABINS } from '@/containers/VoyagePlanner/Summary/constants';
import createMemoSelector from '@/helpers/createMemoSelector';
import { getPriceBreakdownProps } from '@/helpers/data/mappers/Summary';
import { DiscountType } from '@/infra/types/cabin';
import { type TISODateTime } from '@/infra/types/common';

const emptyObj = {};

// Direct selectors:

export const selectSummary = (state: RootState) => state?.voyagePlanner?.summary;

export const selectCabinHold = (state: RootState) => selectSummary(state)?.cabinHold;

export const selectCabinHoldData = (state: RootState) => selectCabinHold(state)?.data;

export const selectCabinHoldDueDateTime = (state: RootState) => selectCabinHoldData(state)?.holdData?.[0]?.dueDateTime;

export const selectIsPayInFullPromoApplied = (state: RootState): boolean =>
  selectSummary(state)?.isPayInFullPromoApplied || false;

export const selectCalculateInvoice = (state: RootState) => selectSummary(state)?.calculateInvoice;
export const selectCalculateInvoiceData = (state: RootState) => selectCalculateInvoice(state)?.data || emptyObj;
export const selectCalculateInvoiceError = (state: RootState) => selectCalculateInvoice(state)?.error;
export const selectCalculateInvoiceErrorCategoryCode = (state: RootState) =>
  selectCalculateInvoiceError(state)?.categoryCode || null;
export const selectCalculateInvoiceErrorSubCategoryCode = (state: RootState) =>
  selectCalculateInvoiceError(state)?.subCategoryCode || null;
export const selectCalculateInvoiceIsLoading = (state: RootState) =>
  selectCalculateInvoice(state)?.isLoading || isEmpty(selectCalculateInvoiceData(state));

export const selectIsCalculateInvoiceLoaded = (state: RootState) => selectCalculateInvoice(state)?.isLoaded;

export const selectCabinInvoices = (state: RootState) => selectCalculateInvoiceData(state)?.cabinInvoices;

export const selectAddOnPriceDetails = (state: RootState) => selectCabinInvoices(state)?.[0]?.addOnPriceDetails;

export const selectBookingPriceDetails = (state: RootState) => selectCabinInvoices(state)?.[0]?.bookingPriceDetails;
export const selectGuestPriceDetails = (state: RootState) => selectCabinInvoices(state)?.[0]?.guestPriceDetails;

export const selectInvoiceCurrencyCode = (state: RootState) => selectBookingPriceDetails(state)?.currencyCode;

export const selectCalculateInvoiceCabinNumber = (state: RootState) => selectCabinInvoices(state)?.[0]?.cabinNumber;

export const selectGuestCount = (state: RootState) => selectCabinInvoices(state)?.[0]?.guestCount;

export const selectFullPayPrice = (state: RootState) => selectCabinInvoices(state)?.[0]?.fullPayPrice;

export const selectPaymentSchedule = (state: RootState) => selectCalculateInvoiceData(state)?.paymentSchedule;

export const selectCabinInvoiceCabinType = (state: RootState) => selectCabinInvoices(state)?.[0]?.cabinTypeCode;
export const selectCabinInvoiceCabinName = (state: RootState) => selectCabinInvoices(state)?.[0]?.name;

// Memoized selectors:

export const selectCabinInvoiceAllowCabinHold = createMemoSelector(
  selectCalculateInvoiceCabinNumber,
  (cabinNumber) => !!cabinNumber && !LOCK_IT_IN_CABINS.includes(cabinNumber!),
);

export const selectPriceBreakdown = createMemoSelector(selectCalculateInvoiceData, (invoice) => {
  if (!invoice.cabinInvoices || !invoice.paymentSchedule) return;

  return getPriceBreakdownProps(invoice.cabinInvoices, invoice.paymentSchedule);
});

export const selectServiceChargeAmount = createSelector(
  [selectBookingPriceDetails],
  (bookingPriceDetails) => bookingPriceDetails?.gratuityAmount || 0,
);

export const selectIsPayInFullPromoAvailable = createSelector(
  selectBookingPriceDetails,
  selectCabinInvoices,
  (bookingPriceDetails, cabinInvoices) => {
    const cabinInvoice = cabinInvoices?.[0];
    return (
      !isEmpty(cabinInvoice?.fullPayPrice) &&
      bookingPriceDetails?.discountList?.findIndex(
        ({ amount, priceTypeText }) => priceTypeText === DiscountType.PNSV && amount < 0,
      ) !== -1
    );
  },
);

export const selectCabinHoldIsReady = createSelector(
  selectIsCalculateInvoiceLoaded,
  selectCabinInvoiceAllowCabinHold,
  selectCabinHoldDueDateTime,
  (isCalculateInvoiceLoaded: boolean, isAllowCabinHold: boolean, dueDateTime?: TISODateTime) =>
    isCalculateInvoiceLoaded && (!isAllowCabinHold || !!dueDateTime),
);

export const selectActualBookingPriceDetails = createSelector(
  selectBookingPriceDetails,
  selectFullPayPrice,
  selectIsPayInFullPromoApplied,
  (bookingPriceDetails, fullPayPrice, isPayInFullPromoApplied) =>
    isPayInFullPromoApplied ? fullPayPrice : bookingPriceDetails,
);

export const selectAddedAddOns = (state: RootState, isPayInFullPromoAppliedWhenAvailable?: boolean) => {
  const priceDetails =
    (isPayInFullPromoAppliedWhenAvailable === true && selectIsPayInFullPromoAvailable(state)) ||
    selectIsPayInFullPromoApplied(state)
      ? selectFullPayPrice(state)
      : selectBookingPriceDetails(state);
  return priceDetails?.addOnPrices;
};
